import { useState, useEffect, useCallback, useRef } from 'react';

// Declaração de tipo para ResizeObserver (pode não estar no lib dom de algumas versões do TS)
declare class ResizeObserver {
    constructor(callback: ResizeObserverCallback);
    observe(target: Element): void;
    unobserve(target: Element): void;
    disconnect(): void;
}

type ResizeObserverCallback = (entries: ResizeObserverEntry[]) => void;

interface ResizeObserverEntry {
    target: Element;
    contentRect: DOMRectReadOnly;
}

const MIN_PAGE_SIZE = 6;
const ROWS_TO_SHOW = 3;

/**
 * Calcula quantos cards cabem por linha baseado na largura do container.
 * Os breakpoints são baseados na largura real do container (não do viewport).
 */
function calculateCardsFromWidth(containerWidth: number): number {
    if (containerWidth <= 0) return 4;

    // Breakpoints baseados na largura do container
    // (aproximados, considerando que container = viewport - sidebar ~250px)
    if (containerWidth >= 1500) return 5;  // viewport >= ~1750
    if (containerWidth >= 1150) return 4;  // viewport >= ~1400
    if (containerWidth >= 742) return 3;   // viewport >= ~992
    if (containerWidth >= 518) return 2;   // viewport >= ~768
    return 1;
}

function calculatePageSize(cardsPerRow: number): number {
    return Math.max(cardsPerRow * ROWS_TO_SHOW, MIN_PAGE_SIZE);
}

/**
 * Hook que calcula dinamicamente o page size para grids de cards
 * baseado na largura real do container, garantindo linhas completas.
 *
 * @returns { pageSize, containerRef } - page size e ref para o container
 */
export function useResponsiveGridPageSize(): {
    pageSize: number;
    containerRef: (node: HTMLDivElement | null) => void;
} {
    const [pageSize, setPageSize] = useState(12);
    const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
    const observerRef = useRef<ResizeObserver | null>(null);

    // Callback ref - chamado quando o elemento é montado/desmontado
    const containerRef = useCallback((node: HTMLDivElement | null) => {
        setContainerNode(node);
    }, []);

    const calculateAndSetPageSize = useCallback((width: number) => {
        const cardsPerRow = calculateCardsFromWidth(width);
        const newPageSize = calculatePageSize(cardsPerRow);
        setPageSize(prev => prev !== newPageSize ? newPageSize : prev);
    }, []);

    useEffect(() => {
        // Limpar observer anterior
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }

        if (containerNode) {
            // Calcular imediatamente
            calculateAndSetPageSize(containerNode.clientWidth);

            // Observar mudanças de tamanho (inclui zoom)
            observerRef.current = new ResizeObserver(entries => {
                for (const entry of entries) {
                    if (entry.target === containerNode) {
                        calculateAndSetPageSize(entry.contentRect.width);
                    }
                }
            });
            observerRef.current.observe(containerNode);
        } else {
            // Fallback quando não há container - usar window
            const width = typeof window !== 'undefined' ? window.innerWidth - 280 : 1200;
            calculateAndSetPageSize(width);

            const handleResize = () => {
                const w = window.innerWidth - 280;
                calculateAndSetPageSize(w);
            };

            let timeoutId: ReturnType<typeof setTimeout>;
            const debouncedResize = () => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(handleResize, 150);
            };

            window.addEventListener('resize', debouncedResize);
            return () => {
                window.removeEventListener('resize', debouncedResize);
                clearTimeout(timeoutId);
            };
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [containerNode, calculateAndSetPageSize]);

    return { pageSize, containerRef };
}

export default useResponsiveGridPageSize;
